<template>
  <el-dialog :title="$t('editFee.title')" :visible.sync="visible" width="520px" :before-close="handleClose">
    <el-alert
      :title="$t('editFee.tipTitle')"
      type="info"
      :closable="false"
      show-icon
      class="edit-fee-tip"
    >
      <div class="tip-line">{{ $t('editFee.tipStartTime') }}</div>
      <div class="tip-line">{{ $t('editFee.tipEndTime') }}</div>
      <div class="tip-line">{{ $t('editFee.tipMaxEndTime') }}</div>
      <div class="tip-line">{{ relationTip }}</div>
    </el-alert>

    <el-form :model="editFeeInfo" label-width="120px" class="edit-fee-form">
      <el-form-item :label="$t('editFee.startTime')" prop="startTime" required>
        <el-date-picker
          v-model="editFeeInfo.startTime"
          type="datetime"
          value-format="yyyy-MM-dd HH:mm:ss"
          :placeholder="$t('editFee.startTimePlaceholder')"
          @change="onTimeChange"
        />
        <div class="field-hint">{{ $t('editFee.startTimeHint') }}</div>
      </el-form-item>

      <el-form-item :label="$t('editFee.endTime')" prop="endTime" required>
        <el-date-picker
          v-model="editFeeInfo.endTime"
          type="date"
          value-format="yyyy-MM-dd"
          :placeholder="$t('editFee.endTimePlaceholder')"
          @change="onTimeChange"
        />
        <div class="field-hint">{{ $t('editFee.endTimeHint') }}</div>
      </el-form-item>

      <el-form-item :label="$t('editFee.maxEndTime')" prop="maxEndTime" required>
        <el-date-picker
          v-model="editFeeInfo.maxEndTime"
          type="date"
          value-format="yyyy-MM-dd"
          :placeholder="$t('editFee.maxEndTimePlaceholder')"
          @change="onTimeChange"
        />
        <div class="field-hint">{{ $t('editFee.maxEndTimeHint') }}</div>
      </el-form-item>

      <template v-if="editFeeInfo.computingFormula === '1102'">
        <el-form-item :label="$t('editFee.rateCycle')" prop="rateCycle">
          <el-input v-model="editFeeInfo.rateCycle" :placeholder="$t('editFee.rateCyclePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('editFee.rate')" prop="rate">
          <el-input v-model="editFeeInfo.rate" :placeholder="$t('editFee.ratePlaceholder')" />
        </el-form-item>
        <el-form-item :label="$t('editFee.rateStartTime')" prop="rateStartTime">
          <el-date-picker
            v-model="editFeeInfo.rateStartTime"
            type="date"
            :placeholder="$t('editFee.rateStartTimePlaceholder')"
            value-format="yyyy-MM-dd"
            @change="validateRateStartTime"
          />
        </el-form-item>
      </template>
    </el-form>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">{{ $t('common.cancel') }}</el-button>
      <el-button type="primary" @click="_doEidtFee">{{ $t('common.submit') }}</el-button>
    </span>
  </el-dialog>
</template>

<script>
import { updateFee } from '@/api/fee/editFeeApi'
import { getCommunityId } from '@/api/community/communityApi'

export default {
  name: 'EditFee',
  data() {
    return {
      visible: false,
      editFeeInfo: {
        feeId: '',
        startTime: '',
        endTime: '',
        feeFlag: '',
        paymentCd: '',
        maxEndTime: '',
        computingFormula: '',
        rateCycle: '',
        rate: '',
        rateStartTime: ''
      }
    }
  },
  computed: {
    isPrepaid() {
      // 1200 预付费；2100 后付费。不再误用 feeFlag（一次性/周期性）
      return this.editFeeInfo.paymentCd === '1200'
    },
    relationTip() {
      if (this.editFeeInfo.paymentCd === '1200') {
        return this.$t('editFee.tipRelationPrepaid')
      }
      if (this.editFeeInfo.paymentCd === '2100') {
        return this.$t('editFee.tipRelationPostpaid')
      }
      return this.$t('editFee.tipRelationUnknown')
    }
  },
  methods: {
    open(fee) {
      const maxEndTime = fee.maxEndTime
        ? String(fee.maxEndTime).split(' ')[0]
        : ''
      this.editFeeInfo = {
        feeId: fee.feeId,
        startTime: fee.startTime.indexOf(':') === -1 ? fee.startTime + ' 00:00:00' : fee.startTime,
        endTime: fee.endTime.split(' ')[0],
        feeFlag: fee.feeFlag,
        paymentCd: fee.paymentCd || '',
        maxEndTime,
        computingFormula: fee.computingFormula,
        rateCycle: fee.rateCycle,
        rate: fee.rate,
        rateStartTime: fee.rateStartTime
      }
      this.visible = true
    },
    handleClose() {
      this.visible = false
      this.clearAddFeeConfigInfo()
    },
    toDate(value) {
      if (!value) return null
      return new Date(String(value).replace(/-/g, '/'))
    },
    onTimeChange() {
      // 改动时只做提示，不强制清空；提交时再严格校验
      const msg = this.getTimeRelationError(false)
      if (msg) {
        this.$message.warning(msg)
      }
    },
    getTimeRelationError(strict) {
      const start = this.toDate(this.editFeeInfo.startTime)
      const end = this.toDate(this.editFeeInfo.endTime)
      const maxEnd = this.toDate(this.editFeeInfo.maxEndTime)

      if (strict) {
        if (!start) return this.$t('editFee.startTimeRequired')
        if (!end) return this.$t('editFee.endTimeRequired')
        if (!maxEnd) return this.$t('editFee.maxEndTimeRequired')
      }
      if (!start || !end) return ''

      // 预付费：建账时间应早于或等于应收开始时间（允许相等）
      // 后付费：建账时间应晚于或等于应收开始时间（允许相等）
      if (this.editFeeInfo.paymentCd === '1200' && start.getTime() > end.getTime()) {
        return this.$t('editFee.startTimeError')
      }
      if (this.editFeeInfo.paymentCd === '2100' && start.getTime() < end.getTime()) {
        return this.$t('editFee.startTimePostError')
      }

      if (maxEnd) {
        // 应收结束必须晚于应收开始，且晚于建账时间
        if (maxEnd.getTime() <= end.getTime()) {
          return this.$t('editFee.maxEndTimeBeforeEndError')
        }
        if (maxEnd.getTime() <= start.getTime()) {
          return this.$t('editFee.maxEndTimeError')
        }
      }
      return ''
    },
    validateRateStartTime(value) {
      const start = this.toDate(this.editFeeInfo.startTime)
      const rateStart = this.toDate(value)
      if (start && rateStart && start.getTime() >= rateStart.getTime()) {
        this.$message.error(this.$t('editFee.rateStartTimeError'))
        this.editFeeInfo.rateStartTime = ''
      }
    },
    editFeeValidate() {
      const msg = this.getTimeRelationError(true)
      if (msg) {
        this.$message.error(msg)
        return false
      }
      return true
    },
    async _doEidtFee() {
      if (!this.editFeeValidate()) return

      this.editFeeInfo.communityId = getCommunityId()

      try {
        const res = await updateFee(this.editFeeInfo)
        if (res.code === 0) {
          this.$message.success(this.$t('common.operationSuccess'))
          this.$emit('success')
          this.handleClose()
        } else {
          this.$message.error(res.msg)
        }
      } catch (error) {
        console.error('请求失败:', error)
        this.$message.error(error.message || this.$t('common.operateFail'))
      }
    },
    clearAddFeeConfigInfo() {
      this.editFeeInfo = {
        feeId: '',
        startTime: '',
        endTime: '',
        feeFlag: '',
        paymentCd: '',
        maxEndTime: '',
        computingFormula: '',
        rateCycle: '',
        rate: '',
        rateStartTime: ''
      }
    }
  }
}
</script>

<style scoped>
.edit-fee-tip {
  margin-bottom: 16px;
  text-align: left;
}

.tip-line {
  line-height: 1.6;
  font-size: 12px;
}

.edit-fee-form {
  text-align: left;
}

.edit-fee-form .el-form-item {
  width: 100%;
}

.edit-fee-form .el-date-editor,
.edit-fee-form .el-input {
  width: 100%;
}

.field-hint {
  margin-top: 4px;
  color: #909399;
  font-size: 12px;
  line-height: 1.4;
}
</style>
